// components/data-quality/DataQualityIssuesMatrix.jsx
import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faInfoCircle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

const DataQualityIssuesMatrix = ({ bookings, onViewDetails }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showNonIssues, setShowNonIssues] = useState(false);

  // Categorize issues
  const issues = useMemo(() => {
    const issuesList = [];

    bookings.forEach((booking) => {
      // Status issues
      if (!booking.statusQuality.isValid) {
        issuesList.push({
          bookingId: booking._id,
          referencia: booking.referencia,
          bookingCode: booking.bookingCode,
          type: "Status Progression",
          description: "Status updates are out of sequence or invalid",
          severity: "High",
          qualityScore: booking.statusQuality.score.toFixed(1) + "%",
          details: booking.statusQuality.message,
        });
      } else if (
        booking.statusQuality.uniqueStatusCount <
        booking.statusQuality.expectedCount
      ) {
        issuesList.push({
          bookingId: booking._id,
          referencia: booking.referencia,
          bookingCode: booking.bookingCode,
          type: "Status Completion",
          description: `Missing ${
            booking.statusQuality.expectedCount -
            booking.statusQuality.uniqueStatusCount
          } status updates`,
          severity: "Medium",
          qualityScore: booking.statusQuality.score.toFixed(1) + "%",
          details: `Has ${booking.statusQuality.uniqueStatusCount} of ${booking.statusQuality.expectedCount} expected statuses`,
        });
      } else if (showNonIssues && booking.statusQuality.isValid) {
        issuesList.push({
          bookingId: booking._id,
          referencia: booking.referencia,
          bookingCode: booking.bookingCode,
          type: "Status",
          description: "Status progression is valid",
          severity: "None",
          qualityScore: booking.statusQuality.score.toFixed(1) + "%",
          details: "All status updates are in the correct sequence",
        });
      }

      // Date issues
      if (booking.dateQuality.dateGapIssues.length > 0) {
        booking.dateQuality.dateGapIssues.forEach((gapIssue) => {
          issuesList.push({
            bookingId: booking._id,
            referencia: booking.referencia,
            bookingCode: booking.bookingCode,
            type: "Date Gap",
            description: `${gapIssue.daysBetween} days between ${gapIssue.fromField} and ${gapIssue.toField}`,
            severity: gapIssue.daysBetween > 14 ? "High" : "Medium",
            qualityScore: booking.dateQuality.score.toFixed(1) + "%",
            details: `From ${new Date(
              gapIssue.fromDate
            ).toLocaleDateString()} to ${new Date(
              gapIssue.toDate
            ).toLocaleDateString()}`,
          });
        });
      } else if (!booking.dateQuality.hasAllRequiredDates) {
        issuesList.push({
          bookingId: booking._id,
          referencia: booking.referencia,
          bookingCode: booking.bookingCode,
          type: "Missing Dates",
          description: `Only ${booking.dateQuality.dateCount} of ${booking.dateQuality.expectedDateCount} required dates present`,
          severity: "Medium",
          qualityScore: booking.dateQuality.score.toFixed(1) + "%",
          details: "Some required date fields are missing",
        });
      } else if (showNonIssues && booking.dateQuality.isValid) {
        issuesList.push({
          bookingId: booking._id,
          referencia: booking.referencia,
          bookingCode: booking.bookingCode,
          type: "Dates",
          description: "Date sequencing is valid",
          severity: "None",
          qualityScore: booking.dateQuality.score.toFixed(1) + "%",
          details: "All required dates are present and properly sequenced",
        });
      }

      // Abandoned booking
      if (booking.isAbandoned) {
        issuesList.push({
          bookingId: booking._id,
          referencia: booking.referencia,
          bookingCode: booking.bookingCode,
          type: "Abandoned",
          description: `No updates for ${booking.dateQuality.daysSinceLastUpdate} days`,
          severity: "Critical",
          qualityScore: booking.overallQualityScore.toFixed(1) + "%",
          details: "Booking appears to be abandoned without completion",
        });
      }
    });

    // Sort by severity (Critical first, then High, Medium, None)
    return issuesList.sort((a, b) => {
      const severityOrder = { Critical: 0, High: 1, Medium: 2, None: 3 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }, [bookings, showNonIssues]);

  // Filter issues based on search query
  const filteredIssues = useMemo(() => {
    if (!searchQuery) return issues;

    const query = searchQuery.toLowerCase();
    return issues.filter(
      (issue) =>
        issue.referencia?.toLowerCase().includes(query) ||
        issue.bookingCode?.toLowerCase().includes(query) ||
        issue.type.toLowerCase().includes(query) ||
        issue.description.toLowerCase().includes(query)
    );
  }, [issues, searchQuery]);

  // Severity badge color
  const getSeverityBadgeClass = (severity) => {
    switch (severity) {
      case "Critical":
        return "bg-red-100 text-red-800 border border-red-200";
      case "High":
        return "bg-orange-100 text-orange-800 border border-orange-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "None":
        return "bg-green-100 text-green-800 border border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  // Type badge color
  const getTypeBadgeClass = (type) => {
    if (type.includes("Status")) {
      return "bg-blue-100 text-blue-800 border border-blue-200";
    } else if (type.includes("Date")) {
      return "bg-purple-100 text-purple-800 border border-purple-200";
    } else if (type === "Abandoned") {
      return "bg-red-100 text-red-800 border border-red-200";
    }
    return "bg-gray-100 text-gray-800 border border-gray-200";
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-orange-600 mr-2"
          />
          Data Quality Issues Matrix
        </h3>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
            </div>
            <input
              type="text"
              className="pl-10 pr-4 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <label className="flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
              checked={showNonIssues}
              onChange={(e) => setShowNonIssues(e.target.checked)}
            />
            Show compliant items
          </label>
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        {filteredIssues.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <FontAwesomeIcon icon={faInfoCircle} className="text-2xl mb-2" />
            <p>No quality issues found matching your criteria.</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Reference
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Issue Description
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Severity
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Quality Score
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredIssues.map((issue, index) => (
                  <tr
                    key={`${issue.bookingId}-${issue.type}-${index}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{issue.referencia}</div>
                      <div className="text-xs text-gray-500">
                        {issue.bookingCode}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeBadgeClass(
                          issue.type
                        )}`}
                      >
                        {issue.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <div>{issue.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {issue.details}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityBadgeClass(
                          issue.severity
                        )}`}
                      >
                        {issue.severity}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {issue.qualityScore}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onViewDetails(issue.bookingId)}
                        className="text-blue-600 hover:text-blue-900 text-sm"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataQualityIssuesMatrix;
